import { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  Activity, Bell, Boxes, Building2, ChevronDown, ClipboardList, Factory,
  Gauge, History, MonitorPlay, Printer, ScanLine, ShieldAlert, Users, Volume2, Wrench,
} from 'lucide-react';
import type { User } from './api';
import { activeNavigationGroup, buildNavigation, type NavigationIcon, type NavigationItem } from './navigation';

const icons = {
  dashboard: Gauge, production: Boxes, notifications: Bell, work: ClipboardList,
  scan: ScanLine, live: Activity, machines: Factory, issues: Wrench, tv: MonitorPlay,
  printing: Printer, announcements: Volume2, users: Users, departments: Building2,
  materials: Boxes, audit: History, history: ClipboardList, security: ShieldAlert,
} satisfies Record<NavigationIcon, typeof Gauge>;

function NavigationLink({ item, onNavigate }: { item: NavigationItem; onNavigate: () => void }) {
  const Icon = icons[item.icon];
  return (
    <NavLink to={item.to} onClick={onNavigate} className={({ isActive }) => isActive ? 'active' : ''}>
      <Icon aria-hidden="true" />
      <span>{item.label}</span>
    </NavLink>
  );
}

export function SidebarNavigation({ user, onNavigate }: { user: User; onNavigate: () => void }) {
  const { pathname } = useLocation();
  const { shortcuts, groups } = buildNavigation(user);
  const activeGroup = activeNavigationGroup(groups, pathname);
  const [selectedGroup, setSelectedGroup] = useState(activeGroup);
  // A new route opens its group; refreshes of auth/permissions do not reopen a group
  // the user deliberately collapsed. State never carries across different users.
  useEffect(() => {
    setSelectedGroup(activeGroup);
  }, [pathname, user.id, activeGroup]);

  return (
    <nav id="main-navigation" aria-label="Điều hướng chính">
      {shortcuts.length > 0 && (
        <div className="sidebar-shortcuts">
          {shortcuts.map(item => <NavigationLink key={item.to} item={item} onNavigate={onNavigate} />)}
        </div>
      )}
      {groups.map(group => {
        const Icon = icons[group.icon];
        const expanded = selectedGroup === group.id;
        const panelId = `sidebar-group-${group.id}`;
        return (
          <div className={`sidebar-group ${activeGroup === group.id ? 'current' : ''}`} key={group.id}>
            <button
              type="button"
              className="sidebar-group-toggle"
              aria-expanded={expanded}
              aria-controls={panelId}
              onClick={() => setSelectedGroup(expanded ? null : group.id)}
            >
              <Icon aria-hidden="true" />
              <span>{group.label}</span>
              <ChevronDown className="sidebar-group-chevron" aria-hidden="true" />
            </button>
            <div id={panelId} className="sidebar-group-items" hidden={!expanded}>
              {group.items.map(item => <NavigationLink key={item.to} item={item} onNavigate={onNavigate} />)}
            </div>
          </div>
        );
      })}
    </nav>
  );
}
